import { Injectable } from '@angular/core';

import { BehaviorSubject, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ECommerce3DSAuthenticationRequest,
  ECommerce3DSRedirectResponse,
  ECommerceCardTokenRequest,
  ECommerceMultipleCardTokensRequest
} from '../api/api.models';
import { ApiService } from '../api/api.service';
import { ErrorService } from '../error/error.service';
import { ExternalData } from '../external-data/external-data.model';
import { ExternalDataService } from '../external-data/external-data.service';
import { uuidV4 } from '../../utils/uuidUtil';

@Injectable({
  providedIn: 'root',
})
export class PanTokenizationService {
  public $isProcessingOrProcessed = new BehaviorSubject(false);
  public $isDoing3DAuth = new BehaviorSubject(false);

  constructor(
    private apiService: ApiService,
    private externalDataService: ExternalDataService,
    private errorService: ErrorService,
  ) {}

  sendPanTokenGenerationWith3DSecure(pan: string, expDateYear: string, expDateMonth: string, cardOwnerName: string) {
    this.externalDataService.$data.pipe(take(1)).subscribe((externalData) => {
      const uuid = uuidV4();
      const url = `${environment.TPS_API}/ecomm/company/${externalData.companyId}/3ds/auth-response`;
      const expiryDateWithSlash = this.compileExpiryDate(expDateYear, expDateMonth);
      const expiryDateWithDash = expDateYear + '-' + expDateMonth; // auth request expects different date format
      const requestData: ECommerce3DSAuthenticationRequest = {
        id: uuid,
        transactionId: externalData.transactionId,
        datetime: externalData.datetime,
        pan: pan,
        expiry: expiryDateWithDash,
        cardholderName: cardOwnerName,
        okUrl: url,
        failUrl: url,
        windowSize: window.innerWidth + 'x' + window.innerHeight,
        hmacFields: externalData.hmacFields,
        hmac: externalData.hmac,
      };

      this.apiService.send3DSAuthRequest(externalData.companyId, 'authentication', requestData).subscribe({
        next: (response) => {
          if (response.redirectMethod === 'POST' && !!response.redirectFormData) {
            this.do3DSecureAuth(response);
            this.waitForAuthResponseAndSendPanTokenGenerationRequest(pan, uuid, expiryDateWithSlash);
          } else {
            this.sendPanTokenGenerationRequestWithUuidAndCardNumber(pan, uuid ,expiryDateWithSlash);
          }
        },
        error: (error) => {
          this.errorService.handleApiError(error);
          this.$isProcessingOrProcessed.next(false);
        },
      });
    });
  }

  sendPanTokenGenerationRequestWithCardNumber(pan: string, expiryDate: string | null) {
    const uuid = this.uuidV4();
    this.sendPanTokenGenerationRequestWithUuidAndCardNumber(pan, uuid, expiryDate);
  }

  sendPanTokenGenerationRequestWithPrn(prn: string, expiryDate: string | null) {
    const uuid = this.uuidV4();
    this.sendPanTokenGenerationRequestWithUuidAndPrn(prn, uuid, expiryDate);
  }

  compileExpiryDate(expDateYear: string, expDateMonth: string) {
    const paddedExpMonth = expDateMonth.length === 1 ? '0' + expDateMonth : expDateMonth;
    return `${paddedExpMonth}/${expDateYear}`;
  }

  private sendPanTokenGenerationRequestWithUuidAndCardNumber(pan: string, uuid: string, expiryDate: string | null) {
    this.externalDataService.$data.pipe(take(1)).subscribe((data) => {
      const hasAdditionalCompanyIds = data.additionalCompanyIds?.length > 0;
      const sendMethod = hasAdditionalCompanyIds
        ? (pan: string, data: ExternalData) => this.sendPanTokenGenerationRequestMultipleTokens(pan, '', data, uuid, expiryDate)
        : (pan: string, data: ExternalData) => this.sendPanTokenGenerationRequestSingleToken(pan, '', data, uuid, expiryDate);

      sendMethod(pan, data).subscribe({
        next: (response) => {
          this.externalDataService.sendParentSuccessResponse(response);
        },
        error: (error) => {
          this.errorService.handleApiError(error);
          this.$isProcessingOrProcessed.next(false);
        }
      });
    });
  }

  private sendPanTokenGenerationRequestWithUuidAndPrn(prn: string, uuid: string, expiryDate: string | null) {
    this.externalDataService.$data.pipe(take(1)).subscribe((data) => {
      const hasAdditionalCompanyIds = data.additionalCompanyIds?.length > 0;
      const sendMethod = hasAdditionalCompanyIds
        ? (prn: string, data: ExternalData) => this.sendPanTokenGenerationRequestMultipleTokens('', prn, data, uuid, expiryDate)
        : (prn: string, data: ExternalData) => this.sendPanTokenGenerationRequestSingleToken('', prn, data, uuid, expiryDate);

      sendMethod(prn, data).subscribe({
        next: (response) => {
          this.externalDataService.sendParentSuccessResponse(response);
        },
        error: (error) => {
          this.errorService.handleApiError(error);
          this.$isProcessingOrProcessed.next(false);
        }
      });
    });
  }

  private sendPanTokenGenerationRequestSingleToken(pan: string, prn: string, data: ExternalData, uuid: string, expiryDate: string | null) {
    const requestDetails: ECommerceCardTokenRequest = {
      authTransactionUuid: uuid,
      companyId: data.companyId,
      epan: pan,
      prn: prn,
      datetime: data.datetime,
      hmacFields: data.hmacFields,
      hmac: data.hmac,
      ...(expiryDate && { expiryDate: expiryDate })
    };

    return this.apiService.doPanTokenGeneration(data.transactionId, requestDetails);
  }

  private sendPanTokenGenerationRequestMultipleTokens(pan: string, prn: string, data: ExternalData, uuid: string, expiryDate: string | null) {
    const companyIds = data.additionalCompanyIds.map((id) => +id);
    const requestDetails: ECommerceMultipleCardTokensRequest = {
      authTransactionUuid: uuid,
      companyId: data.companyId,
      epan: pan,
      prn: prn,
      datetime: data.datetime,
      hmacFields: data.hmacFields,
      hmac: data.hmac,
      companyIds: companyIds,
      ...(expiryDate && { expiryDate: expiryDate })
    };

    return this.apiService.doMultiplePanTokenGeneration(data.transactionId, requestDetails);
  }

  private do3DSecureAuth(authResponse: ECommerce3DSRedirectResponse) {
    this.$isDoing3DAuth.next(true);

    const iframe = document.getElementById('3DSecureFrame') as HTMLIFrameElement;
    const form = iframe.contentWindow?.document.createElement('form');
    form?.setAttribute('method', 'post');
    form?.setAttribute('action', authResponse.redirectUrl);

    Object.entries(authResponse.redirectFormData).forEach(([key, value]) => {
      const input = iframe.contentWindow?.document.createElement('input');
      if (input) {
        input.type = 'hidden';
        input.name = key;

        input.value = String(value);
        form?.appendChild(input);
      }
    });

    if (form) {
      iframe.contentWindow?.document.body.appendChild(form);
      form.submit();
      iframe.contentWindow?.document.body.removeChild(form);
    }
  }

  private waitForAuthResponseAndSendPanTokenGenerationRequest(pan: string, uuid: string, expiryDate: string) {
    window.addEventListener('message', (event) => {
      if (event.target !== window) {
        return;
      }
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.data !== '3DSecureAuthDone') {
        return;
      }

      this.$isDoing3DAuth.next(false);
      this.sendPanTokenGenerationRequestWithUuidAndCardNumber(pan, uuid, expiryDate);
    });
  }

  private uuidV4(): string {
    if (crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // @ts-expect-error - Operator '+' in fact can be applied to types 'number[]' and 'number'
    // But I don't know why, this works and that's all I need
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
  }
}
